import type { EventTicketKind } from "@hobbyshopify/backend-client";
import type { FormEvent } from "react";
import { useObserver, useSubtree } from "subtree.js/react";
import { formatAmount } from "../../../../core/primitives/money";
import {
  capacityLine,
  type TicketKindDraft,
  type TicketKindErrors,
  type TicketKindField,
} from "../../../../core/primitives/ticket-kind";
import { TicketKindsSectionActions, TicketKindsSectionState } from "./ticket-kinds-section-model";

const labelCls = "mb-1.5 block text-xs font-medium text-white";

function KindFields({
  draft,
  errors,
  onChange,
  idPrefix,
  waiting,
}: {
  draft: TicketKindDraft;
  errors: TicketKindErrors;
  onChange: (field: TicketKindField, value: string) => void;
  idPrefix: string;
  /** The form's own call is on its way: what is typed now would be lost with its answer. */
  waiting: boolean;
}) {
  return (
    <>
      <div>
        <label htmlFor={`${idPrefix}-name`} className={labelCls}>
          Kind of ticket
        </label>
        <input
          id={`${idPrefix}-name`}
          value={draft.name}
          onChange={(e) => onChange("name", e.target.value)}
          className="field"
          placeholder="e.g. Day pass"
          maxLength={100}
          readOnly={waiting}
        />
        {errors.name && <p className="field-error">{errors.name}</p>}
      </div>
      <div className="grid gap-2.5 sm:grid-cols-2">
        <div>
          <label htmlFor={`${idPrefix}-price`} className={labelCls}>
            Price
          </label>
          <input
            id={`${idPrefix}-price`}
            value={draft.price}
            onChange={(e) => onChange("price", e.target.value)}
            className="field"
            inputMode="decimal"
            placeholder="5.00"
            readOnly={waiting}
          />
          {errors.price && <p className="field-error">{errors.price}</p>}
        </div>
        <div>
          <label htmlFor={`${idPrefix}-capacity`} className={labelCls}>
            How many <span className="font-normal text-faint">Blank for no limit</span>
          </label>
          <input
            id={`${idPrefix}-capacity`}
            value={draft.capacity}
            onChange={(e) => onChange("capacity", e.target.value)}
            className="field"
            inputMode="numeric"
            readOnly={waiting}
          />
          {errors.capacity && <p className="field-error">{errors.capacity}</p>}
        </div>
      </div>
      <div>
        <label htmlFor={`${idPrefix}-description`} className={labelCls}>
          What it covers <span className="font-normal text-faint">Optional</span>
        </label>
        <input
          id={`${idPrefix}-description`}
          value={draft.description}
          onChange={(e) => onChange("description", e.target.value)}
          className="field"
          placeholder="e.g. Saturday only"
          maxLength={1000}
          readOnly={waiting}
        />
      </div>
    </>
  );
}

function KindRow({
  kind,
  removing,
  acting,
  cancelled,
}: {
  kind: EventTicketKind;
  removing: boolean;
  acting: boolean;
  cancelled: boolean;
}) {
  const actions = useSubtree(TicketKindsSectionActions);
  return (
    <li className="flex flex-wrap items-center gap-3 rounded-row border border-line px-3.5 py-3">
      <div className="min-w-48 flex-1 space-y-0.5">
        <p className="text-[13px] font-medium text-white">
          {kind.name} · {formatAmount(kind.priceCents, kind.currency)}
        </p>
        {kind.description && <p className="text-xs text-muted">{kind.description}</p>}
        <p className="text-xs text-muted">{capacityLine(kind)}</p>
      </div>
      {!cancelled && (
        <div className="flex shrink-0 gap-2">
          <button type="button" className="btn-ghost" disabled={acting} onClick={() => actions.startEdit(kind.id)}>
            Edit
          </button>
          <button type="button" className="btn-ghost" disabled={acting} onClick={() => void actions.remove(kind.id)}>
            {removing ? "Removing…" : "Remove"}
          </button>
        </div>
      )}
    </li>
  );
}

export function TicketKindsSection() {
  const state = useSubtree(TicketKindsSectionState);
  const actions = useSubtree(TicketKindsSectionActions);
  const observer = useObserver();
  const status = observer.watch(state.status);
  const kinds = observer.watch(state.kinds);
  const acting = observer.watch(state.acting);
  const draft = observer.watch(state.draft);
  const errors = observer.watch(state.errors);
  const addForm = observer.watch(state.addForm);
  const editingId = observer.watch(state.editingId);
  const editDraft = observer.watch(state.editDraft);
  const editErrors = observer.watch(state.editErrors);
  const editForm = observer.watch(state.editForm);
  const removingId = observer.watch(state.removingId);
  const removeError = observer.watch(state.removeError);
  const cancelled = observer.watch(state.cancelled);

  const add = (e: FormEvent) => {
    e.preventDefault();
    void actions.add();
  };
  const save = (e: FormEvent) => {
    e.preventDefault();
    void actions.saveEdit();
  };

  return (
    <section className="panel space-y-4 p-4">
      <div>
        <h2 className="text-base font-semibold">Tickets</h2>
        <p className="text-sm opacity-60">
          What visitors pay to come in. Tickets are not on sale through HobbyScan yet: what you add here is ready for
          when they are.
        </p>
      </div>

      {status.kind === "loading" && kinds.length === 0 && <p className="opacity-60">Loading the tickets…</p>}
      {status.kind === "error" && <p className="note note-danger">{status.message}</p>}
      {removeError && <p className="note note-danger">{removeError}</p>}

      {kinds.length > 0 && (
        <ul className="space-y-2">
          {kinds.map((kind) =>
            editingId === kind.id ? (
              <li key={kind.id}>
                <form onSubmit={save} className="flex flex-col gap-2.5 rounded-row border border-line p-3.5">
                  <KindFields
                    draft={editDraft}
                    errors={editErrors}
                    onChange={actions.setEditField}
                    idPrefix={`ticket-kind-${kind.id}`}
                    waiting={editForm.busy}
                  />
                  {editForm.error && <p className="note note-danger">{editForm.error}</p>}
                  <div className="flex justify-end gap-2">
                    <button type="button" className="btn-ghost" disabled={acting} onClick={actions.cancelEdit}>
                      Cancel
                    </button>
                    <button type="submit" className="btn-primary btn-sm" disabled={acting}>
                      {editForm.busy ? "Saving…" : "Save"}
                    </button>
                  </div>
                </form>
              </li>
            ) : (
              <KindRow
                key={kind.id}
                kind={kind}
                removing={removingId === kind.id}
                acting={acting}
                cancelled={cancelled}
              />
            ),
          )}
        </ul>
      )}

      {status.kind === "loaded" && !cancelled && (
        <form onSubmit={add} className="flex flex-col gap-2.5 rounded-row border border-line p-3.5">
          <p className="text-[13px] font-semibold text-white">Add a kind of ticket</p>
          <KindFields
            draft={draft}
            errors={errors}
            onChange={actions.setField}
            idPrefix="ticket-kind-new"
            waiting={addForm.busy}
          />
          {addForm.error && <p className="note note-danger">{addForm.error}</p>}
          <button type="submit" className="btn-primary btn-sm self-start" disabled={acting}>
            {addForm.busy ? "Adding…" : "+ Add tickets"}
          </button>
        </form>
      )}
    </section>
  );
}
